import {
  Component,
  ContentChild,
  Input,
  OnChanges,
  OnInit,
  TemplateRef,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { AbstractControlOptions, UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';

import { Subject } from 'rxjs';

import { SvgLibraryIcon } from '@finnairoyj/fcom-ui-styles';

import { ButtonTheme } from '@fcom/ui-components/components/buttons/button/enums';
import { IconPosition } from '@fcom/ui-components/components/icons/enums';

type UntypedGroupConfig = Parameters<UntypedFormBuilder['group']>[0];

@Component({
  selector: 'fin-form-array',
  templateUrl: 'form-array.component.html',
  styleUrls: ['form-array.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormArrayComponent implements OnInit, OnChanges {
  private fb = inject(UntypedFormBuilder);

  readonly SvgLibraryIcon = SvgLibraryIcon;
  readonly IconPosition = IconPosition;
  readonly ButtonTheme = ButtonTheme;

  @Input()
  parentForm: UntypedFormGroup;

  @Input()
  controlName: string;

  @Input()
  translationPrefix: string;

  @Input()
  formGroupConfig: UntypedGroupConfig;

  @Input()
  formGroupOptions: AbstractControlOptions = {};

  @Input()
  minGroups = 0;

  @Input()
  maxGroups = 100;

  @Input()
  hideGroups = false;

  @Input()
  displayLabels = false;

  @Input()
  isNestedArray = false;

  @Input()
  displayContainerStyles = true;

  @Input()
  compressedList = false;

  @Input()
  disabled = false;

  @ContentChild(TemplateRef, { static: true }) formGroup: TemplateRef<unknown>;

  focusToButton$: Subject<boolean> = new Subject<boolean>();

  ngOnInit(): void {
    for (let i = this.formArray.length; i < this.minGroups; i++) {
      this.formArray.push(this.fb.group(this.formGroupConfig, this.formGroupOptions));
    }
  }

  ngOnChanges(): void {
    while (this.maxGroups < this.formArray.length) {
      this.formArray.removeAt(this.formArray.length - 1);
    }
  }

  addFormGroup(): void {
    if (this.formArray.length < this.maxGroups) {
      this.formArray.push(this.fb.group(this.formGroupConfig, this.formGroupOptions));
      this.parentForm.markAsDirty();
    }
  }

  removeFormGroup(index: number): void {
    this.focusToButton$.next(true);
    this.formArray.removeAt(index);
    this.parentForm.markAsDirty();
  }

  get formArray(): UntypedFormArray {
    return this.parentForm.get(this.controlName) as UntypedFormArray;
  }
}
